/*
 * Audioralis hero bento — hover video.
 * Shopify-hosted video is in the DOM already (preload="none"), so hovering
 * just plays it. YouTube/Vimeo get their iframe built on first hover, which
 * keeps three third-party players off the critical path of the homepage.
 */
(() => {
  /*
   * YouTube paints its own furniture over anything that is not actively
   * playing — the title, the channel name, a grid of related thumbnails and
   * the big play button. controls=0 does not suppress it and rel=0 stopped
   * meaning "no related videos" in 2018; it only limits them to the same
   * channel. So the iframe is uncovered only while the clip is demonstrably
   * running, and the player's own PLAYING flag does not count as
   * demonstrable: a refused autoplay reports PLAYING and pauses again 75ms
   * later at currentTime 0.1, again and again, and each of those flickers
   * used to put Erica Synths' related videos across the hero. The clock has
   * to actually move before anything is revealed.
   */
  const CONFIRM_AT = 0.6; // seconds of real playback before uncovering
  const CONFIRM_WINDOW = 6000; // stop waiting for that clock after this long
  const MAX_FALSE_STARTS = 3; // pauses inside the first second = autoplay refused

  const embed = (url) => {
    try {
      const u = new URL(url);
      if (/youtube|youtu\.be/.test(u.hostname)) {
        const id = u.hostname.includes('youtu.be') ? u.pathname.slice(1) : u.searchParams.get('v');
        if (!id) return null;
        // mute=1 is not optional — a homepage that makes noise is a homepage
        // people close. cc_load_policy=0 keeps captions off even for viewers
        // whose YouTube account has them switched on by default, and
        // iv_load_policy=3 drops annotation cards over the picture.
        const p = new URLSearchParams({
          autoplay: '1',
          mute: '1',
          loop: '1',
          playlist: id,
          controls: '0',
          playsinline: '1',
          modestbranding: '1',
          rel: '0',
          fs: '0',
          cc_load_policy: '0',
          iv_load_policy: '3',
          disablekb: '1',
          // lets us hear onStateChange over postMessage without pulling in
          // YouTube's iframe_api script
          enablejsapi: '1',
          origin: location.origin,
        });
        return { kind: 'youtube', src: `https://www.youtube-nocookie.com/embed/${id}?${p}` };
      }
      if (u.hostname.includes('vimeo')) {
        const id = u.pathname.split('/').filter(Boolean).pop();
        if (!id) return null;
        // background=1 already means muted, no controls, no title cards — there
        // is no furniture to hide from, so this one is safe to show on load
        return { kind: 'vimeo', src: `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&loop=1&background=1&texttrack=false` };
      }
    } catch (e) {
      /* a malformed URL just means no video */
    }
    return null;
  };

  /*
   * Follow a YouTube player over postMessage and hold the tile to one rule:
   * the frame is visible only while the clip is genuinely running. Three
   * false starts and we take the player out altogether — a browser that has
   * refused autoplay three times is not going to change its mind, and the
   * photograph underneath is a better hero than a paused YouTube page.
   */
  const watch = (tile, frame, onDead) => {
    let time = -1; // last currentTime the player reported
    let falseStarts = 0;
    let confirmTimer = null;
    let ran = false; // has it ever played for real
    let dead = false;

    const send = (func, args = []) => {
      try {
        frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func, args }), '*');
      } catch (e) {
        /* frame gone */
      }
    };

    const cover = () => tile.classList.remove('is-playing');
    const stopConfirm = () => {
      if (confirmTimer) clearInterval(confirmTimer);
      confirmTimer = null;
    };

    // PLAYING arrives long before the first frame is painted, so poll the
    // clock the player keeps sending us and uncover once it has really moved
    const confirm = () => {
      if (confirmTimer || dead) return;
      const since = Date.now();
      confirmTimer = setInterval(() => {
        if (time >= CONFIRM_AT) {
          stopConfirm();
          ran = true;
          if (!dead) tile.classList.add('is-playing');
          return;
        }
        // a client that never sends a clock at all: stop burning the timer,
        // leave the photograph up rather than gamble on the overlay
        if (Date.now() - since > CONFIRM_WINDOW) stopConfirm();
      }, 150);
    };

    const onMessage = (e) => {
      if (dead || e.source !== frame.contentWindow) return;
      let host;
      try {
        host = new URL(e.origin).hostname;
      } catch (err) {
        return;
      }
      if (!/(^|\.)youtube(-nocookie)?\.com$/.test(host)) return;
      let data;
      try {
        data = JSON.parse(e.data);
      } catch (err) {
        return;
      }

      // infoDelivery carries the clock several times a second, usually with no
      // state attached; onStateChange carries a bare number
      const info = data && data.info;
      let state;
      if (info && typeof info === 'object') {
        if (typeof info.currentTime === 'number') time = info.currentTime;
        state = info.playerState;
      } else if (data && data.event === 'onStateChange') {
        state = info;
      }
      if (typeof state !== 'number') return;

      if (state === 1) {
        confirm(); // PLAYING — verify it, don't take its word
        return;
      }
      // a buffer stall mid-clip freezes the picture, it does not draw the
      // overlay, so flashing back to the photograph would be the worse jump
      if (state === 3 && ran) return;

      stopConfirm();
      cover();

      if (state === 0) {
        // ENDED — loop=1 otherwise parks the hero on the channel's outro card
        time = -1;
        send('seekTo', [0, true]);
        send('playVideo');
        return;
      }
      if (state === 2 && time < 1) {
        falseStarts += 1;
        if (falseStarts >= MAX_FALSE_STARTS) kill();
      }
    };

    const kill = () => {
      dead = true;
      stopConfirm();
      cover();
      window.removeEventListener('message', onMessage);
      frame.remove();
      if (onDead) onDead();
    };

    window.addEventListener('message', onMessage);
    // the player only starts reporting once asked to
    frame.addEventListener('load', () => {
      try {
        frame.contentWindow.postMessage(JSON.stringify({ event: 'listening' }), '*');
      } catch (e) {
        /* frame gone */
      }
      send('playVideo');
    });

    return { send, isDead: () => dead };
  };

  const wire = (tile) => {
    if (tile.dataset.videoWired) return;
    tile.dataset.videoWired = '1';

    const native = tile.querySelector('video.aud-btile__video');
    const external = tile.querySelector('.aud-btile__video--external');
    let player = null; // the third-party controller, once one exists

    const build = () => {
      const spec = embed(external.dataset.videoUrl);
      if (!spec) return;
      const frame = document.createElement('iframe');
      frame.className = 'aud-btile__video';
      frame.src = spec.src;
      frame.allow = 'autoplay; encrypted-media';
      frame.setAttribute('tabindex', '-1');
      frame.setAttribute('aria-hidden', 'true');
      external.replaceWith(frame);
      external.dataset.loaded = '1';

      if (spec.kind === 'vimeo') {
        // nothing to hide behind: background=1 has no chrome in any state
        frame.addEventListener('load', () => tile.classList.add('is-playing'));
        player = { send: () => {}, isDead: () => false };
        return;
      }
      player = watch(tile, frame, () => {
        player = null;
        tile.dataset.videoRefused = '1';
      });
    };

    const start = () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (native) {
        native.muted = true;
        const p = native.play();
        // autoplay can still be refused; don't blank the image if it is
        if (p && p.catch) p.catch(() => tile.classList.remove('is-playing'));
        tile.classList.add('is-playing');
        return;
      }
      if (tile.dataset.videoRefused) return; // this browser will not play it
      // built already: ask it to resume. The tile is uncovered by the watcher
      // when the clip is actually running and never on our say-so — the old
      // blanket reveal here is what put a paused YouTube page on the hero.
      if (player) {
        if (!player.isDead()) player.send('playVideo');
        return;
      }
      if (external && !external.dataset.loaded) build();
    };

    const stop = () => {
      tile.classList.remove('is-playing');
      if (native) {
        native.pause();
        native.currentTime = 0;
        return;
      }
      if (player && !player.isDead()) player.send('pauseVideo');
    };

    if (tile.hasAttribute('data-autoplay')) {
      // runs from load and stays running — no hover handlers, or moving the
      // mouse across the hero would stop the thing it came to look at
      start();
      return;
    }

    tile.addEventListener('mouseenter', start);
    tile.addEventListener('mouseleave', stop);
    tile.addEventListener('focus', start);
    tile.addEventListener('blur', stop);
  };

  const init = (root = document) => root.querySelectorAll('.aud-btile[data-has-video]').forEach(wire);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
